import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

// Bootstrap Modal型定義（LoginModal.tsxと同じ）

interface RegisterModalProps {
  onRegisterSuccess?: () => void;
}

/**
 * 新規登録モーダルコンポーネント
 * SPLICEデザインに合ったモダンな登録機能を提供
 */
const RegisterModal: React.FC<RegisterModalProps> = ({ onRegisterSuccess }) => {
  const { register, loading } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    password_confirm: ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [showPassword, setShowPassword] = useState(false);

  // フォーム入力の変更処理
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // エラーをクリア
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // 新規登録処理
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // バリデーション
    const newErrors: {[key: string]: string} = {};
    if (!formData.username.trim()) {
      newErrors.username = 'ユーザー名を入力してください';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'メールアドレスを入力してください';
    }
    if (!formData.first_name.trim()) {
      newErrors.first_name = '名前を入力してください';
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = '苗字を入力してください';
    }
    if (!formData.password) {
      newErrors.password = 'パスワードを入力してください';
    } else if (formData.password.length < 8) {
      newErrors.password = 'パスワードは8文字以上で入力してください';
    }
    if (formData.password !== formData.password_confirm) {
      newErrors.password_confirm = 'パスワードが一致しません';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // 登録実行
    const success = await register(formData);
    if (success) {
      // 登録成功時の処理
      setFormData({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        password_confirm: ''
      });
      onRegisterSuccess?.();
      
      // モーダルを閉じる
      const modal = document.getElementById('registerModal');
      if (modal) {
        const modalInstance = window.bootstrap?.Modal?.getInstance(modal);
        modalInstance?.hide();
      }
    } else {
      setErrors({ general: '登録に失敗しました。入力内容を確認してください。' });
    }
  };

  // パスワード表示切り替え
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="modal fade" id="registerModal" tabIndex={-1} aria-labelledby="registerModalLabel" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content modern-auth-modal">
          
          {/* ヘッダー */}
          <div className="modern-auth-header">
            <div className="auth-logo">
              <img 
                src="/img/splice_logo.png" 
                alt="SPLICE" 
                className="logo-image"
              />
            </div>
            <button
              type="button"
              className="modern-close-btn"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* コンテンツ */}
          <div className="modern-auth-body">
            <div className="auth-title">
              <h2>新規アカウント作成</h2>
            </div>

            {loading ? (
              <div className="auth-loading">
                <div className="loading-spinner"></div>
                <span>アカウント作成中...</span>
              </div>
            ) : (
              <form onSubmit={handleRegister} className="auth-form">
                {/* エラーメッセージ */}
                {errors.general && (
                  <div className="auth-error">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 1.33333L14.6667 14H1.33333L8 1.33333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 6V8.66667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 11.3333C8.36819 11.3333 8.66667 11.0348 8.66667 10.6667C8.66667 10.2985 8.36819 10 8 10C7.63181 10 7.33333 10.2985 7.33333 10.6667C7.33333 11.0348 7.63181 11.3333 8 11.3333Z" fill="currentColor"/>
                    </svg>
                    {errors.general}
                  </div>
                )}

                <div className="row g-3">
                  {/* ユーザー名 */}
                  <div className="col-12">
                    <div className="form-group">
                      <label>ユーザー名</label>
                      <div className="input-wrapper">
                        <svg className="input-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M8 8C10.2091 8 12 6.20914 12 4C12 1.79086 10.2091 0 8 0C5.79086 0 4 1.79086 4 4C4 6.20914 5.79086 8 8 8Z" fill="currentColor"/>
                          <path d="M8 10C3.58172 10 0 13.5817 0 18H16C16 13.5817 12.4183 10 8 10Z" fill="currentColor"/>
                        </svg>
                        <input
                          type="text"
                          className={errors.username ? 'error' : ''}
                          name="username"
                          value={formData.username}
                          onChange={handleInputChange}
                          placeholder="ユーザー名"
                          disabled={loading}
                        />
                      </div>
                      {errors.username && (
                        <div className="text-danger small mt-1">{errors.username}</div>
                      )}
                    </div>
                  </div>

                  {/* 名前 */}
                  <div className="col-12">
                    <div className="form-group">
                      <label>名前</label>
                      <div className="input-wrapper">
                        <svg className="input-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M12 14V12.6667C12 11.9594 11.719 11.2811 11.219 10.781C10.719 10.281 10.0406 10 9.33333 10H6.66667C5.95942 10 5.28115 10.281 4.78105 10.781C4.28095 11.2811 4 11.9594 4 12.6667V14" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M8 7.33333C9.47276 7.33333 10.6667 6.13943 10.6667 4.66667C10.6667 3.19391 9.47276 2 8 2C6.52724 2 5.33333 3.19391 5.33333 4.66667C5.33333 6.13943 6.52724 7.33333 8 7.33333Z" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <input
                          type="text"
                          className={errors.first_name ? 'error' : ''}
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleInputChange}
                          placeholder="太郎"
                          disabled={loading}
                        />
                      </div>
                      {errors.first_name && (
                        <div className="text-danger small mt-1">{errors.first_name}</div>
                      )}
                    </div>
                  </div>

                  {/* 苗字 */}
                  <div className="col-12">
                    <div className="form-group">
                      <label>苗字</label>
                      <div className="input-wrapper">
                        <svg className="input-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M12 14V12.6667C12 11.9594 11.719 11.2811 11.219 10.781C10.719 10.281 10.0406 10 9.33333 10H6.66667C5.95942 10 5.28115 10.281 4.78105 10.781C4.28095 11.2811 4 11.9594 4 12.6667V14" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M8 7.33333C9.47276 7.33333 10.6667 6.13943 10.6667 4.66667C10.6667 3.19391 9.47276 2 8 2C6.52724 2 5.33333 3.19391 5.33333 4.66667C5.33333 6.13943 6.52724 7.33333 8 7.33333Z" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <input
                          type="text"
                          className={errors.last_name ? 'error' : ''}
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleInputChange}
                          placeholder="田中"
                          disabled={loading}
                        />
                      </div>
                      {errors.last_name && (
                        <div className="text-danger small mt-1">{errors.last_name}</div>
                      )}
                    </div>
                  </div>

                  {/* メールアドレス */}
                  <div className="col-12">
                    <div className="form-group">
                      <label>メールアドレス</label>
                      <div className="input-wrapper">
                        <svg className="input-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M2.66667 2.66667H13.3333C14.0667 2.66667 14.6667 3.26667 14.6667 4V12C14.6667 12.7333 14.0667 13.3333 13.3333 13.3333H2.66667C1.93333 13.3333 1.33333 12.7333 1.33333 12V4C1.33333 3.26667 1.93333 2.66667 2.66667 2.66667Z" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14.6667 4L8 8.66667L1.33333 4" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <input
                          type="email"
                          className={errors.email ? 'error' : ''}
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="example@domain.com"
                          disabled={loading}
                        />
                      </div>
                      {errors.email && (
                        <div className="text-danger small mt-1">{errors.email}</div>
                      )}
                    </div>
                  </div>

                  {/* パスワード */}
                  <div className="col-12">
                    <div className="form-group">
                      <label>パスワード</label>
                      <div className="input-wrapper">
                        <svg className="input-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M12.6667 7.33333H3.33333C2.59695 7.33333 2 7.93029 2 8.66667V13.3333C2 14.0697 2.59695 14.6667 3.33333 14.6667H12.6667C13.403 14.6667 14 14.0697 14 13.3333V8.66667C14 7.93029 13.403 7.33333 12.6667 7.33333Z" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M4.66667 7.33333V4.66667C4.66667 3.78261 5.01786 2.93477 5.64298 2.30964C6.26811 1.68452 7.11595 1.33333 8 1.33333C8.88406 1.33333 9.73189 1.68452 10.357 2.30964C10.9821 2.93477 11.3333 3.78261 11.3333 4.66667V7.33333" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <input
                          type={showPassword ? "text" : "password"}
                          className={errors.password ? 'error' : ''}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="8文字以上"
                          disabled={loading}
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={togglePasswordVisibility}
                        >
                          {showPassword ? (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M9.41 8L8 6.59L6.59 8L8 9.41L9.41 8ZM8 2C11.87 2 15.38 4.86 16 8C15.38 11.14 11.87 14 8 14C4.13 14 0.62 11.14 0 8C0.62 4.86 4.13 2 8 2ZM8 12C10.5 12 12.59 10.43 13.17 8.17C12.59 5.57 10.5 4 8 4C5.5 4 3.41 5.57 2.83 8.17C3.41 10.43 5.5 12 8 12Z" fill="currentColor"/>
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M14.12 14.12L1.88 1.88L2.59 1.17L14.83 13.41L14.12 14.12ZM8 2C11.87 2 15.38 4.86 16 8C15.63 9.61 14.8 11.06 13.65 12.17L12.22 10.74C13.07 9.95 13.67 9.03 13.95 8C13.17 5.57 10.5 4 8 4C7.36 4 6.75 4.11 6.18 4.31L4.69 2.82C5.74 2.3 6.86 2 8 2ZM8 14C4.13 14 0.62 11.14 0 8C0.37 6.39 1.2 4.94 2.35 3.83L3.78 5.26C2.93 6.05 2.33 6.97 2.05 8C2.83 10.43 5.5 12 8 12C8.64 12 9.25 11.89 9.82 11.69L11.31 13.18C10.26 13.7 9.14 14 8 14Z" fill="currentColor"/>
                            </svg>
                          )}
                        </button>
                      </div>
                      {errors.password && (
                        <div className="text-danger small mt-1">{errors.password}</div>
                      )}
                    </div>
                  </div>

                  {/* パスワード確認 */}
                  <div className="col-12">
                    <div className="form-group">
                      <label>パスワード確認</label>
                      <div className="input-wrapper">
                        <svg className="input-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M12.6667 7.33333H3.33333C2.59695 7.33333 2 7.93029 2 8.66667V13.3333C2 14.0697 2.59695 14.6667 3.33333 14.6667H12.6667C13.403 14.6667 14 14.0697 14 13.3333V8.66667C14 7.93029 13.403 7.33333 12.6667 7.33333Z" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M4.66667 7.33333V4.66667C4.66667 3.78261 5.01786 2.93477 5.64298 2.30964C6.26811 1.68452 7.11595 1.33333 8 1.33333C8.88406 1.33333 9.73189 1.68452 10.357 2.30964C10.9821 2.93477 11.3333 3.78261 11.3333 4.66667V7.33333" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <input
                          type="password"
                          className={errors.password_confirm ? 'error' : ''}
                          name="password_confirm"
                          value={formData.password_confirm}
                          onChange={handleInputChange}
                          placeholder="パスワードを再入力"
                          disabled={loading}
                        />
                      </div>
                      {errors.password_confirm && (
                        <div className="text-danger small mt-1">{errors.password_confirm}</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* 登録ボタン */}
                <button
                  type="submit"
                  className="auth-submit-btn"
                  disabled={loading}
                >
                  アカウント作成
                </button>

                {/* ログインリンク */}
                <div className="auth-switch">
                  <span>既にアカウントをお持ちの方</span>
                  <button
                    type="button"
                    className="auth-switch-btn"
                    data-bs-toggle="modal"
                    data-bs-target="#loginModal"
                    data-bs-dismiss="modal"
                  >
                    ログイン
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterModal; 